import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  QueryDocumentSnapshot,
  DocumentData, 
  serverTimestamp,
  writeBatch,
  addDoc,
  CollectionReference
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, uploadBytesResumable } from 'firebase/storage';
import { db, storage, auth } from '../firebase/config';
import { User, UserRole, Attachment, Lesson, Course, CourseCode, CourseLevel, CourseSubject } from '../types';
import { createUserWithEmailAndPassword, deleteUser as deleteFirebaseUser } from 'firebase/auth';

// كاش للمستخدمين
interface UserCacheItem {
  data: User;
  timestamp: number;
}

interface UsersListCacheItem {
  data: User[];
  timestamp: number;
}

const userCache: Record<string, UserCacheItem> = {};
const usersListCache: Record<string, UsersListCacheItem> = {};
const USER_CACHE_EXPIRY = 5 * 60 * 1000; // 5 دقائق
const USERS_LIST_CACHE_EXPIRY = 2 * 60 * 1000; // دقيقتان

// دالة للحصول على مستخدم مع التخزين المؤقت
export const getUser = async (userId: string): Promise<User | null> => {
  try {
    // التحقق من وجود البيانات في الكاش وما إذا كانت صالحة
    const now = Date.now();
    if (userCache[userId] && now - userCache[userId].timestamp < USER_CACHE_EXPIRY) {
      console.log(`استخدام بيانات المستخدم المخزنة مؤقتًا للمستخدم: ${userId}`);
      return userCache[userId].data;
    }
    
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (userDoc.exists()) {
      const userData = {
        id: userDoc.id,
        ...userDoc.data()
      } as User;
      
      // تخزين البيانات في الكاش
      userCache[userId] = {
        data: userData,
        timestamp: now
      };
      
      return userData;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

// دالة للحصول على قائمة المستخدمين مع التخزين المؤقت
export const getUsers = async (
  role?: UserRole, 
  isVerified?: boolean,
  lastDoc?: QueryDocumentSnapshot<DocumentData>,
  pageSize: number = 10
): Promise<{ users: User[], lastVisible: QueryDocumentSnapshot<DocumentData> | null }> => {
  try {
    // إنشاء مفتاح للكاش بناءً على المعلمات
    const cacheKey = `${role || 'all'}_${isVerified !== undefined ? isVerified : 'any'}_${pageSize}`;
    const now = Date.now();
    
    // التحقق من وجود البيانات في الكاش وما إذا كانت صالحة
    if (!lastDoc && usersListCache[cacheKey] && now - usersListCache[cacheKey].timestamp < USERS_LIST_CACHE_EXPIRY) {
      console.log(`استخدام قائمة المستخدمين المخزنة مؤقتًا للمفتاح: ${cacheKey}`);
      return { 
        users: usersListCache[cacheKey].data,
        lastVisible: null // لا يمكننا تخزين lastVisible في الكاش لأنه كائن معقد
      };
    }
    
    // إنشاء استعلام أساسي
    let usersRef = collection(db, 'users') as CollectionReference<DocumentData>;
    let constraints: any[] = [];
    
    // إضافة قيود الاستعلام
    if (role) {
      constraints.push(where('role', '==', role));
    }
    
    if (isVerified !== undefined) {
      constraints.push(where('isVerified', '==', isVerified));
    }
    
    // إضافة الترتيب والحد
    constraints.push(orderBy('createdAt', 'desc'));
    
    // إنشاء الاستعلام النهائي
    let usersQuery = query(usersRef, ...constraints, limit(pageSize));
    
    // إضافة lastDoc إذا كان موجودًا (للصفحات التالية)
    if (lastDoc) {
      usersQuery = query(usersRef, ...constraints, startAfter(lastDoc), limit(pageSize));
    }
    
    const usersSnapshot = await getDocs(usersQuery);
    const users: User[] = [];
    
    usersSnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      } as User);
    });
    
    // تخزين البيانات في الكاش (فقط للصفحة الأولى)
    if (!lastDoc) {
      usersListCache[cacheKey] = {
        data: users,
        timestamp: now
      };
    }
    
    return {
      users,
      lastVisible: usersSnapshot.docs.length > 0 ? usersSnapshot.docs[usersSnapshot.docs.length - 1] : null
    };
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

// دالة لإلغاء صلاحية الكاش عند تحديث المستخدم
export const invalidateUserCache = (userId: string) => {
  // إلغاء صلاحية كاش المستخدم المحدد
  if (userCache[userId]) {
    delete userCache[userId];
  }
  
  // إلغاء صلاحية جميع قوائم المستخدمين المخزنة مؤقتًا
  for (const key in usersListCache) {
    delete usersListCache[key];
  }
};

// تحديث المستخدم مع إلغاء صلاحية الكاش
export const updateUser = async (userId: string, userData: Partial<User>): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...userData,
      updatedAt: Date.now()
    });
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(userId);
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// حذف المستخدم مع إلغاء صلاحية الكاش
export const deleteUser = async (userId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'users', userId));
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(userId);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Get all users with pagination
export const getAllUsers = async (limitCount = 20, startAfterDoc?: QueryDocumentSnapshot<User>) => {
  try {
    let usersQuery;
    
    if (startAfterDoc) {
      usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        startAfter(startAfterDoc),
        limit(limitCount)
      );
    } else {
      usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    }
    
    const usersSnapshot = await getDocs(usersQuery);
    const users: User[] = [];
    
    usersSnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      } as User);
    });
    
    const lastVisible = usersSnapshot.docs.length > 0 ? usersSnapshot.docs[usersSnapshot.docs.length - 1] : null;
    
    return {
      users,
      lastVisible
    };
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

// Get users by role
export const getUsersByRole = async (role: UserRole, limitCount = 20, startAfterDoc?: QueryDocumentSnapshot<User>) => {
  try {
    let usersQuery;
    
    if (startAfterDoc) {
      usersQuery = query(
        collection(db, 'users'),
        where('role', '==', role),
        orderBy('createdAt', 'desc'),
        startAfter(startAfterDoc),
        limit(limitCount)
      );
    } else {
      usersQuery = query(
        collection(db, 'users'),
        where('role', '==', role),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    }
    
    const usersSnapshot = await getDocs(usersQuery);
    const users: User[] = [];
    
    usersSnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      } as User);
    });
    
    const lastVisible = usersSnapshot.docs.length > 0 ? usersSnapshot.docs[usersSnapshot.docs.length - 1] : null;
    
    return {
      users,
      lastVisible
    };
  } catch (error) {
    console.error('Error getting users by role:', error);
    throw error;
  }
};

// Get user by ID
export const getUserById = async (id: string) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', id));
    
    if (userDoc.exists()) {
      return {
        id: userDoc.id,
        ...userDoc.data()
      } as User;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user by ID:', error);
    throw error;
  }
};

// إنشاء حساب مدرس جديد
export const createTeacher = async (
  name: string,
  phoneNumber: string,
  password: string
): Promise<User> => {
  try {
    // إنشاء بريد إلكتروني عشوائي للمدرس (لأغراض التسجيل فقط)
    const randomEmail = `teacher_${Date.now()}@example.com`;
    
    // إنشاء حساب المستخدم في Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, randomEmail, password);
    const uid = userCredential.user.uid;
    
    // إنشاء بيانات المدرس في Firestore
    const teacherData: User = {
      id: uid,
      name,
      phoneNumber,
      email: randomEmail,
      role: UserRole.TEACHER,
      isVerified: true,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    await setDoc(doc(db, 'users', uid), teacherData);
    
    return teacherData;
  } catch (error) {
    console.error('Error creating teacher:', error);
    throw error;
  }
};

// جلب قائمة المستخدمين حسب الدور
export const getUsersByRoleList = async (role: UserRole): Promise<User[]> => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('role', '==', role)
    );
    
    const usersSnapshot = await getDocs(usersQuery);
    const users: User[] = [];
    
    usersSnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      } as User);
    });
    
    return users;
  } catch (error) {
    console.error(`Error getting users by role (${role}):`, error);
    throw error;
  }
};

// Update user
export const updateUserProfile = async (id: string, userData: Partial<User>, profilePicture?: File) => {
  try {
    const userRef = doc(db, 'users', id);
    
    // تحديث صورة الملف الشخصي إذا تم تقديمها
    if (profilePicture) {
      const storageRef = ref(storage, `users/${id}/profile-picture`);
      await uploadBytes(storageRef, profilePicture);
      const photoURL = await getDownloadURL(storageRef);
      
      userData.photoURL = photoURL;
    }
    
    // تحديث بيانات المستخدم
    await updateDoc(userRef, {
      ...userData,
      updatedAt: Date.now()
    });
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(id);
    
    return {
      success: true,
      message: 'User profile updated successfully'
    };
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Upload ID proof for verification
export const uploadIdProof = async (id: string, idProofFile: File) => {
  try {
    const storageRef = ref(storage, `users/${id}/id-proof`);
    await uploadBytes(storageRef, idProofFile);
    const idProofURL = await getDownloadURL(storageRef);
    
    await updateDoc(doc(db, 'users', id), {
      idProofURL,
      idProofSubmittedAt: Date.now(),
      updatedAt: Date.now()
    });
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(id);
    
    return {
      success: true,
      message: 'ID proof uploaded successfully'
    };
  } catch (error) {
    console.error('Error uploading ID proof:', error);
    throw error;
  }
};

// Verify user
export const verifyUser = async (id: string, isVerified: boolean) => {
  try {
    await updateDoc(doc(db, 'users', id), {
      isVerified,
      verifiedAt: isVerified ? Date.now() : null,
      updatedAt: Date.now()
    });
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(id);
    
    return {
      success: true,
      message: `User ${isVerified ? 'verified' : 'unverified'} successfully`
    };
  } catch (error) {
    console.error('Error verifying user:', error);
    throw error;
  }
};

// Delete user account completely
export const deleteUserAccountCompletely = async (userId: string): Promise<void> => {
  try {
    // حذف حساب المستخدم من Firebase Authentication
    await deleteFirebaseUser(auth.currentUser!);
    
    // حذف بيانات المستخدم من Firestore
    await deleteDoc(doc(db, 'users', userId));
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(userId);
  } catch (error) {
    console.error('Error deleting user account completely:', error);
    throw error;
  }
};

// جلب إحصائيات المستخدمين
export const getUserStats = async () => {
  try {
    // جلب إجمالي عدد المستخدمين
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const totalUsers = usersSnapshot.size;
    
    // جلب عدد المستخدمين حسب الدور
    const teachersQuery = query(collection(db, 'users'), where('role', '==', UserRole.TEACHER));
    const teachersSnapshot = await getDocs(teachersQuery);
    const totalTeachers = teachersSnapshot.size;
    
    const studentsQuery = query(collection(db, 'users'), where('role', '==', UserRole.STUDENT));
    const studentsSnapshot = await getDocs(studentsQuery);
    const totalStudents = studentsSnapshot.size;
    
    const adminsQuery = query(collection(db, 'users'), where('role', '==', UserRole.ADMIN));
    const adminsSnapshot = await getDocs(adminsQuery);
    const totalAdmins = adminsSnapshot.size;
    
    // جلب عدد المستخدمين المتحقق منهم
    const verifiedUsersQuery = query(collection(db, 'users'), where('isVerified', '==', true));
    const verifiedUsersSnapshot = await getDocs(verifiedUsersQuery);
    const totalVerifiedUsers = verifiedUsersSnapshot.size;
    
    const unverifiedUsers = totalUsers - totalVerifiedUsers;
    
    // تقسيم المستخدمين غير المتحققين بين المعلمين والطلاب بشكل تقريبي
    const unverifiedTeachers = Math.floor(unverifiedUsers * (totalTeachers / totalUsers));
    const unverifiedStudents = unverifiedUsers - unverifiedTeachers;
    
    return {
      totalUsers,
      totalTeachers,
      totalStudents,
      totalAdmins,
      totalVerifiedUsers,
      unverifiedUsers,
      teachers: {
        total: totalTeachers,
        verified: totalTeachers - unverifiedTeachers,
        pending: unverifiedTeachers
      },
      students: {
        total: totalStudents,
        verified: totalStudents - unverifiedStudents,
        pending: unverifiedStudents
      }
    };
  } catch (error) {
    console.error('Error getting user stats:', error);
    throw error;
  }
};

// Function to fetch students assigned to a teacher
export const getStudents = async (teacherId: string): Promise<User[]> => {
  try {
    // جلب قائمة الطلاب المرتبطين بالمدرس
    const studentsQuery = query(
      collection(db, 'users'),
      where('role', '==', UserRole.STUDENT),
      where('teacherId', '==', teacherId)
    );
    
    const studentsSnapshot = await getDocs(studentsQuery);
    const students: User[] = [];
    
    studentsSnapshot.forEach((doc) => {
      students.push({
        id: doc.id,
        ...doc.data()
      } as User);
    });
    
    return students;
  } catch (error) {
    console.error('Error getting students for teacher:', error);
    throw error;
  }
};

// Function to fetch assignments for a teacher
export const getAssignments = async (teacherId: string): Promise<any[]> => {
  try {
    // جلب قائمة الواجبات المرتبطة بالمدرس
    const assignmentsQuery = query(
      collection(db, 'assignments'),
      where('teacherId', '==', teacherId)
    );
    
    const assignmentsSnapshot = await getDocs(assignmentsQuery);
    const assignments: any[] = [];
    
    assignmentsSnapshot.forEach((doc) => {
      assignments.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return assignments;
  } catch (error) {
    console.error('Error getting assignments for teacher:', error);
    throw error;
  }
};

// Function to add a new assignment
export const addAssignment = async (assignmentData: { name: string; teacherId: string }) => {
  try {
    const assignmentRef = collection(db, 'assignments');
    const newAssignmentDoc = await addDoc(assignmentRef, {
      ...assignmentData,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
    
    return {
      success: true,
      assignmentId: newAssignmentDoc.id,
      message: 'Assignment added successfully'
    };
  } catch (error) {
    console.error('Error adding assignment:', error);
    throw error;
  }
};

// Function to update an existing assignment
export const updateAssignment = async (assignmentId: string, assignmentData: any) => {
  try {
    const assignmentRef = doc(db, 'assignments', assignmentId);
    
    await updateDoc(assignmentRef, {
      ...assignmentData,
      updatedAt: Date.now()
    });
    
    return {
      success: true,
      message: 'Assignment updated successfully'
    };
  } catch (error) {
    console.error('Error updating assignment:', error);
    throw error;
  }
};

// Function to delete an assignment
export const deleteAssignment = async (assignmentId: string) => {
  try {
    await deleteDoc(doc(db, 'assignments', assignmentId));
    
    return {
      success: true,
      message: 'Assignment deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting assignment:', error);
    throw error;
  }
};

// القيم الثابتة للتحقق من الملفات
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 ميجابايت
const ALLOWED_FILE_TYPES = {
  // المستندات
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  
  // الصور
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  
  // الملفات المضغوطة
  'application/zip': 'ZIP',
  
  // ملفات النصوص
  'text/plain': 'TXT'
};

// كاش لتخزين الملفات المرفقة
class AttachmentCacheItem {
  data: Attachment[] = [];
  timestamp: number = Date.now();
}

const attachmentsCache: Record<string, AttachmentCacheItem> = {};
const ATTACHMENTS_CACHE_EXPIRY = 5 * 60 * 1000; // 5 دقائق

// دالة مساعدة لتنسيق حجم الملف بشكل مقروء
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' bytes';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
};

// دالة للتحقق من صحة الملف
export const validateFile = (file: File): { valid: boolean; error?: string } => {
  // التحقق من حجم الملف
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `حجم الملف كبير جدًا. الحد الأقصى هو ${formatFileSize(MAX_FILE_SIZE)}.`
    };
  }
  
  // التحقق من نوع الملف
  if (!Object.keys(ALLOWED_FILE_TYPES).includes(file.type)) {
    return {
      valid: false,
      error: `نوع الملف غير مدعوم. الأنواع المدعومة هي: ${Object.values(ALLOWED_FILE_TYPES).join(', ')}.`
    };
  }
  
  return { valid: true };
};

// دالة جديدة للحصول على مرفقات الدرس مع التخزين المؤقت
export const getLessonAttachments = async (courseId: string, lessonId: string): Promise<Attachment[]> => {
  try {
    const cacheKey = `${courseId}_${lessonId}`;
    const now = Date.now();
    
    // التحقق من وجود البيانات في الكاش وما إذا كانت صالحة
    if (attachmentsCache[cacheKey] && now - attachmentsCache[cacheKey].timestamp < ATTACHMENTS_CACHE_EXPIRY) {
      console.log(`استخدام مرفقات الدرس المخزنة مؤقتًا للدرس: ${lessonId}`);
      return attachmentsCache[cacheKey].data;
    }
    
    const lessonDoc = await getDoc(doc(db, 'lessons', lessonId));
    
    if (!lessonDoc.exists()) {
      throw new Error('Lesson not found');
    }
    
    const lessonData = lessonDoc.data();
    const attachments = lessonData.attachments || [];
    
    // تخزين البيانات في الكاش
    attachmentsCache[cacheKey] = {
      data: attachments,
      timestamp: now
    };
    
    return attachments;
  } catch (error) {
    console.error('Error getting lesson attachments:', error);
    throw error;
  }
};

// دالة للحصول على إحصائيات لوحة معلومات المعلم
export const getTeacherDashboardStats = async (teacherId: string) => {
  try {
    // الحصول على دورات المعلم
    const coursesRef = collection(db, 'courses');
    const coursesQuery = query(coursesRef, where('teacherId', '==', teacherId));
    const coursesSnapshot = await getDocs(coursesQuery);
    
    // إحصائيات أولية
    let totalStudents = 0;
    let totalCourses = coursesSnapshot.size;
    let totalLessons = 0;
    let totalRevenue = 0;
    
    // جمع معرفات الدورات
    const courseIds: string[] = [];
    coursesSnapshot.forEach(doc => {
      courseIds.push(doc.id);
      
      // إضافة عدد الطلاب المسجلين في كل دورة
      const courseData = doc.data();
      totalStudents += courseData.enrolledCount || 0;
      totalLessons += courseData.lessonsCount || 0;
      totalRevenue += (courseData.price || 0) * (courseData.enrolledCount || 0);
    });
    
    // الحصول على التسجيلات الأخيرة
    const enrollmentsRef = collection(db, 'enrollments');
    const enrollmentsQuery = query(
      enrollmentsRef,
      where('courseId', 'in', courseIds.length > 0 ? courseIds : ['no-courses']),
      orderBy('enrollmentDate', 'desc'),
      limit(5)
    );
    
    const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
    const recentEnrollments: any[] = [];
    
    enrollmentsSnapshot.forEach(doc => {
      recentEnrollments.push({
        id: doc.id,
        ...doc.data() as Record<string, any>
      });
    });
    
    return {
      totalStudents,
      totalCourses,
      totalLessons,
      totalRevenue,
      recentEnrollments
    };
  } catch (error) {
    console.error('Error getting teacher dashboard stats:', error);
    throw error;
  }
};

// دالة للتحقق من صلاحيات المستخدم
export const checkUserPermissions = async (userId: string, requiredRole: UserRole): Promise<boolean> => {
  try {
    const user = await getUser(userId);
    
    if (!user) {
      return false;
    }
    
    // التحقق من الدور
    if (user.role === UserRole.ADMIN) {
      // المسؤول لديه جميع الصلاحيات
      return true;
    }
    
    // التحقق من تطابق الدور المطلوب
    return user.role === requiredRole;
  } catch (error) {
    console.error('Error checking user permissions:', error);
    return false;
  }
};

// دالة للحصول على معلومات المستخدم الحالي
export const getCurrentUserInfo = async (userId: string) => {
  try {
    const user = await getUser(userId);
    
    if (!user) {
      throw new Error('User not found');
    }
    
    // إذا كان المستخدم مدرسًا، نحصل على إحصائيات لوحة المعلومات
    if (user.role === UserRole.TEACHER) {
      const stats = await getTeacherDashboardStats(userId);
      
      return {
        user,
        stats
      };
    }
    
    // إذا كان المستخدم طالبًا، نحصل على الدورات المسجلة
    if (user.role === UserRole.STUDENT) {
      const enrollmentsRef = collection(db, 'enrollments');
      const enrollmentsQuery = query(
        enrollmentsRef,
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      const enrollments: any[] = [];
      
      enrollmentsSnapshot.forEach(doc => {
        enrollments.push({
          id: doc.id,
          ...doc.data() as Record<string, any>
        });
      });
      
      return {
        user,
        enrollments
      };
    }
    
    // للمسؤول، نعيد فقط معلومات المستخدم
    return { user };
  } catch (error) {
    console.error('Error getting current user info:', error);
    throw error;
  }
};

// دالة للتحقق من وجود المستخدم بواسطة البريد الإلكتروني
export const checkUserExists = async (email: string): Promise<boolean> => {
  try {
    const usersRef = collection(db, 'users');
    const usersQuery = query(usersRef, where('email', '==', email));
    const usersSnapshot = await getDocs(usersQuery);
    
    return !usersSnapshot.empty;
  } catch (error) {
    console.error('Error checking if user exists:', error);
    throw error;
  }
};

// دالة لتحديث صورة الملف الشخصي للمستخدم
export const updateProfilePicture = async (userId: string, file: File): Promise<string> => {
  try {
    // التحقق من صحة الملف
    const validation = validateFile(file);
    if (!validation.valid) {
      throw new Error(validation.error || 'Invalid file');
    }
    
    // إنشاء اسم فريد للملف
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `profile_${timestamp}.${fileExtension}`;
    
    // مسار الملف في Firebase Storage
    const filePath = `users/${userId}/profile/${fileName}`;
    const fileRef = ref(storage, filePath);
    
    // تحميل الملف
    await uploadBytes(fileRef, file);
    
    // الحصول على URL للتنزيل
    const downloadURL = await getDownloadURL(fileRef);
    
    // تحديث معلومات المستخدم - تحديث كلا الحقلين photoURL و profilePicture
    await updateDoc(doc(db, 'users', userId), {
      photoURL: downloadURL,
      profilePicture: downloadURL,
      updatedAt: Date.now()
    });
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(userId);
    
    return downloadURL;
  } catch (error) {
    console.error('Error updating profile picture:', error);
    throw error;
  }
};

// دالة للحصول على قائمة المستخدمين حسب الدور مع البحث
export const searchUsersByRole = async (role: UserRole, searchTerm: string): Promise<User[]> => {
  try {
    // جلب جميع المستخدمين بالدور المحدد
    const usersRef = collection(db, 'users');
    const usersQuery = query(usersRef, where('role', '==', role));
    const usersSnapshot = await getDocs(usersQuery);
    
    const users: User[] = [];
    
    usersSnapshot.forEach(doc => {
      const userData = doc.data() as Record<string, any>;
      
      // البحث في اسم المستخدم والبريد الإلكتروني
      if (
        searchTerm === '' ||
        userData.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        userData.email?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        users.push({
          id: doc.id,
          ...userData
        } as User);
      }
    });
    
    return users;
  } catch (error) {
    console.error('Error searching users by role:', error);
    throw error;
  }
};

// دالة لتغيير دور المستخدم
export const changeUserRole = async (userId: string, newRole: UserRole): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      role: newRole,
      updatedAt: Date.now()
    });
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(userId);
  } catch (error) {
    console.error('Error changing user role:', error);
    throw error;
  }
};

// دالة لإنشاء مستخدم جديد
export const createUser = async (userData: Partial<User>, password: string): Promise<User> => {
  try {
    // التحقق من وجود المستخدم
    const exists = await checkUserExists(userData.email || '');
    
    if (exists) {
      throw new Error('User with this email already exists');
    }
    
    // إنشاء المستخدم في Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email || '',
      password
    );
    
    const userId = userCredential.user.uid;
    
    // إنشاء وثيقة المستخدم في Firestore
    const newUser: User = {
      id: userId,
      name: userData.name || '',
      email: userData.email || '',
      role: userData.role || UserRole.STUDENT,
      isVerified: false,
      isActive: true,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      ...userData
    };
    
    await setDoc(doc(db, 'users', userId), newUser);
    
    return newUser;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// دالة لتعطيل حساب المستخدم
export const deactivateUser = async (userId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      isActive: false,
      updatedAt: Date.now()
    });
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(userId);
  } catch (error) {
    console.error('Error deactivating user:', error);
    throw error;
  }
};

// دالة لتفعيل حساب المستخدم
export const activateUser = async (userId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      isActive: true,
      updatedAt: Date.now()
    });
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(userId);
  } catch (error) {
    console.error('Error activating user:', error);
    throw error;
  }
};

// دالة لتبديل حالة المستخدم (تفعيل/تعطيل)
export const toggleUserStatus = async (userId: string, isActive: boolean): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      isActive,
      updatedAt: Date.now()
    });
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(userId);
  } catch (error) {
    console.error('Error toggling user status:', error);
    throw error;
  }
};

// دالة لحذف حساب المستخدم
export const deleteUserAccount = async (userId: string): Promise<void> => {
  try {
    // حذف المستخدم من Firestore
    await deleteDoc(doc(db, 'users', userId));
    
    // إلغاء صلاحية الكاش
    invalidateUserCache(userId);
  } catch (error) {
    console.error('Error deleting user account:', error);
    throw error;
  }
};

// دالة للحصول على الدورات حسب المدرس
export const getCoursesByTeacher = async (teacherId: string): Promise<Course[]> => {
  try {
    const coursesRef = collection(db, 'courses');
    const coursesQuery = query(
      coursesRef,
      where('teacherId', '==', teacherId),
      orderBy('createdAt', 'desc')
    );
    
    const coursesSnapshot = await getDocs(coursesQuery);
    const courses: Course[] = [];
    
    coursesSnapshot.forEach(doc => {
      courses.push({
        id: doc.id,
        ...doc.data() as Record<string, any>
      } as Course);
    });
    
    return courses;
  } catch (error) {
    console.error('Error getting courses by teacher:', error);
    throw error;
  }
};

// دالة لإضافة دورة جديدة
export const addCourse = async (courseData: Partial<Course>): Promise<string> => {
  try {
    // إنشاء معرف فريد للدورة
    const courseRef = doc(collection(db, 'courses'));
    const courseId = courseRef.id;
    
    // إعداد بيانات الدورة
    const course: Course = {
      id: courseId,
      title: courseData.title || '',
      description: courseData.description || '',
      teacherId: courseData.teacherId || '',
      teacherName: courseData.teacherName || '',
      imageUrl: courseData.imageUrl || '',
      price: courseData.price || 0,
      level: courseData.level || CourseLevel.BEGINNER,
      subject: courseData.subject || CourseSubject.OTHER,
      duration: courseData.duration || 0,
      isPublished: courseData.isPublished || false,
      isFeatured: courseData.isFeatured || false,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    // حفظ الدورة في Firestore
    await setDoc(courseRef, course);
    
    return courseId;
  } catch (error) {
    console.error('Error adding course:', error);
    throw error;
  }
};

// دالة لإضافة درس إلى دورة
export const addLessonToCourse = async (
  courseId: string,
  lessonData: Partial<Lesson>
): Promise<string> => {
  try {
    // إنشاء معرف فريد للدرس
    const lessonRef = doc(collection(db, 'lessons'));
    const lessonId = lessonRef.id;
    
    // إعداد بيانات الدرس
    const lesson: Lesson = {
      id: lessonId,
      courseId,
      title: lessonData.title || '',
      description: lessonData.description || '',
      videoUrl: lessonData.videoUrl || '',
      duration: lessonData.duration || 0,
      order: lessonData.order || 0,
      attachments: lessonData.attachments || [],
      isPublished: lessonData.isPublished || false,
      isFree: lessonData.isFree || false,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    // حفظ الدرس في Firestore
    await setDoc(lessonRef, lesson);
    
    // تحديث الدورة بإضافة معرف الدرس
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (courseDoc.exists()) {
      const courseData = courseDoc.data();
      const lessons = courseData.lessons || [];
      
      await updateDoc(courseRef, {
        lessons: [...lessons, lessonId],
        updatedAt: serverTimestamp()
      });
      
      return lessonId;
    } else {
      throw new Error('الدورة غير موجودة');
    }
  } catch (error) {
    console.error('Error adding lesson to course:', error);
    throw error;
  }
};

// دالة لتحديث معلومات الدرس
export const updateLessonInfo = async (
  lessonId: string,
  lessonData: Partial<Lesson>
): Promise<void> => {
  try {
    const lessonRef = doc(db, 'lessons', lessonId);
    const lessonDoc = await getDoc(lessonRef);
    
    if (lessonDoc.exists()) {
      // تحديث الدرس
      await updateDoc(lessonRef, {
        ...lessonData,
        updatedAt: Date.now()
      });
    } else {
      throw new Error('الدرس غير موجود');
    }
  } catch (error) {
    console.error('Error updating lesson:', error);
    throw error;
  }
};

// دالة لحذف درس من دورة
export const deleteLessonFromCourse = async (
  courseId: string,
  lessonId: string
): Promise<void> => {
  try {
    // حذف المرفقات أولاً
    const lessonRef = doc(db, 'lessons', lessonId);
    const lessonDoc = await getDoc(lessonRef);
    
    if (lessonDoc.exists()) {
      const lessonData = lessonDoc.data();
      const attachments = lessonData.attachments || [];
      
      // حذف جميع المرفقات من التخزين
      const deletePromises = attachments.map(async (attachment: Attachment) => {
        if (attachment.path) {
          const fileRef = ref(storage, attachment.path);
          try {
            await deleteObject(fileRef);
          } catch (error) {
            console.warn(`Could not delete file ${attachment.name}:`, error);
          }
        }
      });
      
      await Promise.all(deletePromises);
      
      // حذف الدرس من Firestore
      await deleteDoc(lessonRef);
      
      // تحديث الدورة بإزالة معرف الدرس
      const courseRef = doc(db, 'courses', courseId);
      const courseDoc = await getDoc(courseRef);
      
      if (courseDoc.exists()) {
        const courseData = courseDoc.data();
        const lessons = courseData.lessons || [];
        
        await updateDoc(courseRef, {
          lessons: lessons.filter((id: string) => id !== lessonId),
          updatedAt: serverTimestamp()
        });
      }
    } else {
      throw new Error('الدرس غير موجود');
    }
  } catch (error) {
    console.error('Error deleting lesson from course:', error);
    throw error;
  }
};

// دالة لتحديث بيانات الدورة
export const updateCourse = async (
  courseId: string,
  courseData: Partial<Course>
): Promise<void> => {
  try {
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (courseDoc.exists()) {
      // تحديث الدورة
      await updateDoc(courseRef, {
        ...courseData,
        updatedAt: Date.now()
      });
    } else {
      throw new Error('الدورة غير موجودة');
    }
  } catch (error) {
    console.error('Error updating course:', error);
    throw error;
  }
};

// دالة لحذف دورة
export const deleteCourse = async (courseId: string): Promise<void> => {
  try {
    // الحصول على الدورة للتأكد من وجودها
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (courseDoc.exists()) {
      const courseData = courseDoc.data() as Record<string, any>;
      const lessons = courseData.lessons || [];
      
      // حذف جميع الدروس المرتبطة بالدورة
      const deletePromises = lessons.map(async (lessonId: string) => {
        try {
          // استخدام الدالة الموجودة لحذف الدرس والمرفقات الخاصة به
          await deleteLessonFromCourse(courseId, lessonId);
        } catch (error) {
          console.warn(`Could not delete lesson ${lessonId}:`, error);
        }
      });
      
      await Promise.all(deletePromises);
      
      // حذف صورة الدورة من التخزين إذا كانت موجودة
      if (courseData.imageUrl) {
        try {
          // استخراج المسار من URL الصورة
          const imageRef = ref(storage, `courses/${courseId}/image`);
          await deleteObject(imageRef);
        } catch (error) {
          console.warn('Could not delete course image:', error);
        }
      }
      
      // حذف الدورة من Firestore
      await deleteDoc(courseRef);
    } else {
      throw new Error('الدورة غير موجودة');
    }
  } catch (error) {
    console.error('Error deleting course:', error);
    throw error;
  }
};

// دالة لتحميل صورة الدورة
export const uploadCourseImage = async (
  file: File,
  courseId?: string,
  onProgress?: (progress: number) => void
): Promise<string> => {
  try {
    // التحقق من صحة الملف
    const validationResult = validateFile(file);
    
    if (!validationResult.valid) {
      throw new Error(validationResult.error || 'ملف غير صالح');
    }
    
    // إنشاء مسار التخزين
    const storagePath = courseId 
      ? `courses/${courseId}/image` 
      : `courses/temp/${Date.now()}_${file.name}`;
    
    const storageRef = ref(storage, storagePath);
    
    // تحميل الملف مع تتبع التقدم
    if (onProgress) {
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress(progress);
        },
        (error) => {
          console.error('Error uploading course image:', error);
          throw error;
        }
      );
      
      // انتظار اكتمال التحميل
      await uploadTask;
    } else {
      // تحميل بدون تتبع التقدم
      await uploadBytes(storageRef, file);
    }
    
    // الحصول على URL للصورة
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading course image:', error);
    throw error;
  }
};